import { useState, useEffect } from "react";
import { FiAlertTriangle, FiCheck, FiX, FiLink, FiClock } from "react-icons/fi";
import Modal from "newui/components/ui/Modal/Modal";
import type { ClarificationReviewSummary } from "../../../../types/clarification-review.type";

interface StepCompleteModalProps {
	isLoading: boolean;
	isOpen: boolean;
	summary: ClarificationReviewSummary;
	onClose: () => void;
	onConfirm: (dateDue?: string) => void;
}

export const StepCompleteModal: React.FC<StepCompleteModalProps> = ({
	isOpen,
	summary,
	onClose,
	onConfirm,
	isLoading,
}) => {
	const [dateDue, setDateDue] = useState("");

	const hasPending = summary.pending > 0;
	const hasNoClarifications = summary.total === 0;
	const allDeclined = summary.total > 0 && summary.accepted === 0 && summary.pending === 0;
	const hasAccepted = summary.accepted > 0;

	useEffect(() => {
		if (isOpen) {
			// Default to 7 days from now
			const defaultDate = new Date();
			defaultDate.setDate(defaultDate.getDate() + 7);
			setDateDue(defaultDate.toISOString().split("T")[0]);
		}
	}, [isOpen]);

	const handleConfirm = () => {
		onConfirm(hasAccepted && dateDue ? dateDue : undefined);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="lg"
			title="Complete Clarification Review"
			description="Finalise the clarification review step and proceed to the next phase."
			modalType="standard"
			iconType={hasPending || allDeclined ? "warning" : "info"}
			closeOnEsc
			closeOnOverlayClick={!isLoading}
			primaryButton={{
				onClick: handleConfirm,
				text: isLoading ? "Completing..." : "Complete Review Step",
				size: "sm",
				width: "full",
				disabled: isLoading || (hasAccepted && !dateDue),
			}}
			secondaryButton={{
				onClick: onClose,
				text: "Cancel",
				size: "sm",
				width: "full",
			}}
		>
			<div className="space-y-4" data-testid="step-complete-modal">
				{/* Warning for pending items */}
				{hasPending && (
					<div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="pending-warning">
						<div className="flex items-start gap-2">
							<FiAlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={16} />
							<div className="text-sm text-amber-800">
								<p className="font-medium">
									{summary.pending} clarification{summary.pending !== 1 ? "s" : ""} still pending
								</p>
								<p className="mt-0.5">
									Pending clarifications will not be sent to suppliers. You can go back and review them, or proceed to skip them.
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Warning when all declined */}
				{allDeclined && (
					<div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="all-declined-warning">
						<div className="flex items-start gap-2">
							<FiAlertTriangle className="mt-0.5 shrink-0 text-red-600" size={16} />
							<div className="text-sm text-red-800">
								<p className="font-medium">No clarifications will be sent to suppliers</p>
								<p className="mt-0.5">
									All clarifications have been declined or marked as duplicate. No formal clarification requests will be sent.
								</p>
							</div>
						</div>
					</div>
				)}

				{/* No clarifications info */}
				{hasNoClarifications && (
					<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
						<p className="text-sm text-gray-600">
							No clarifications were proposed. You can proceed to the next step.
						</p>
					</div>
				)}

				{/* Review summary */}
				{!hasNoClarifications && (
					<div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="review-summary">
						<p className="mb-3 text-sm font-medium text-gray-700">Review Summary</p>
						<div className="grid grid-cols-2 gap-3">
							<div className="flex items-center gap-2">
								<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
									<FiCheck size={12} className="text-green-600" />
								</span>
								<span className="text-sm text-gray-600">
									<span className="font-medium">{summary.accepted}</span> accepted
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
									<FiX size={12} className="text-red-600" />
								</span>
								<span className="text-sm text-gray-600">
									<span className="font-medium">{summary.declined}</span> declined
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
									<FiLink size={12} className="text-gray-600" />
								</span>
								<span className="text-sm text-gray-600">
									<span className="font-medium">{summary.duplicate}</span> duplicate
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
									<FiClock size={12} className="text-amber-600" />
								</span>
								<span className="text-sm text-gray-600">
									<span className="font-medium">{summary.pending}</span> pending
								</span>
							</div>
						</div>
						<div className="mt-3 border-t border-gray-200 pt-3">
							<p className="text-sm text-gray-500">
								Total: <span className="font-medium text-gray-700">{summary.total}</span> clarification{summary.total !== 1 ? "s" : ""} |
								Reviewed: <span className="font-medium text-gray-700">{summary.reviewed}</span> of {summary.total}
							</p>
						</div>
					</div>
				)}

				{/* Response deadline — only when there are accepted clarifications to send */}
				{hasAccepted && (
					<div data-testid="deadline-section">
						<label htmlFor="step-complete-date-due" className="mb-1 block text-sm font-medium text-gray-700">
							Supplier Response Deadline
						</label>
						<input
							id="step-complete-date-due"
							type="date"
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
							value={dateDue}
							onChange={(e) => setDateDue(e.target.value)}
							min={new Date().toISOString().split("T")[0]}
							data-testid="date-due-input"
						/>
						<p className="mt-1 text-xs text-gray-400">
							Suppliers will have until this date to respond to the accepted clarifications.
						</p>
					</div>
				)}

				{/* What happens next */}
				<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
					<p className="text-sm font-medium text-blue-800">What happens next:</p>
					<ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-blue-700">
						{hasAccepted && <li>Accepted clarifications will be sent to their respective suppliers</li>}
						{(summary.declined > 0 || summary.duplicate > 0) && (
							<li>Declined and duplicate clarifications will be archived</li>
						)}
						<li>This step will be marked as complete and locked</li>
					</ul>
				</div>
			</div>
		</Modal>
	);
};
